import { apiErrorMessage, customFetch } from '@agos/api-client'
import { blockKindLabel, customerBlockOf, customerBlockPath, T, tt } from '@agos/shared'
import { ConfirmDialog, toast } from '@agos/ui'
import { useQuery } from '@tanstack/react-query'
import { Ban, ShieldOff } from 'lucide-react'
import { useState } from 'react'

/**
 * Banning a customer from the drawer - `FRONTEND.md` §9.787, on BACKEND.md §10A.439, owner O140: *"ban specific user
 * from zákazníci"*.
 *
 * *Zablokovať zákazníka* blocks their e-mail and phone: no web order from either, as a guest too, and no sign-in.
 * *Odblokovať* takes off every block on their current contacts and every one made from them. Shown to the holder of
 * `marketing_customers_deactivate`; the server answers whether they are blocked, so the button cannot say the
 * opposite of what is stored.
 */
export function CustomerBlockSection({ customerId }: { customerId: number }) {
  const read = useQuery({
    queryKey: ['customer-block', customerId],
    queryFn: () => customFetch<unknown>(customerBlockPath(customerId)),
  })
  const state = customerBlockOf(read.data)
  const [asking, setAsking] = useState(false)
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)

  const write = async (block: boolean) => {
    setSaving(true)
    try {
      await customFetch<unknown>(customerBlockPath(customerId), {
        method: block ? 'POST' : 'DELETE',
        ...(block
          ? {
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ reason: reason.trim() === '' ? null : reason.trim() }),
            }
          : {}),
      })
      toast({ title: block ? tt('Zákazník zablokovaný') : tt('Zákazník odblokovaný') })
      setAsking(false)
      setReason('')
      await read.refetch()
    } catch (caught) {
      toast({
        variant: 'destructive',
        title: block ? tt('Zablokovať sa nepodarilo') : tt('Odblokovať sa nepodarilo'),
        description: apiErrorMessage(caught, tt('Skúste to znova.')),
      })
    }
    setSaving(false)
  }

  if (read.isLoading) return null
  if (state === null) {
    return (
      <p className="text-[11px] text-red-500" data-customer-block-failed>
        <T>Nepodarilo sa zistiť, či je zákazník zablokovaný.</T>
      </p>
    )
  }

  return (
    <div className="space-y-1.5" data-customer-block={state.blocked ? 'blocked' : 'open'}>
      {state.blocked ? (
        <>
          <p className="text-xs font-semibold text-red-500">
            <T>Zablokovaný</T>:{' '}
            {state.entries.map((entry) => `${blockKindLabel(entry.kind)} ${entry.shown}`).join(', ')}
          </p>
          <button
            type="button"
            data-customer-unblock
            disabled={saving}
            onClick={() => void write(false)}
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border border-border text-sm font-semibold hover:bg-muted/50 disabled:opacity-50"
          >
            <ShieldOff className="w-4 h-4" />
            <T>Odblokovať</T>
          </button>
        </>
      ) : (
        <button
          type="button"
          data-customer-block-open
          onClick={() => setAsking(true)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border border-red-500/40 text-red-500 text-sm font-semibold hover:bg-red-500/10"
        >
          <Ban className="w-4 h-4" />
          <T>Zablokovať zákazníka</T>
        </button>
      )}
      <p className="text-[11px] text-muted-foreground">
        <T>
          Zablokovaný zákazník neodošle objednávku z webu - ani ako hosť s rovnakým e-mailom alebo telefónom - a
          neprihlási sa. Objednávku cez telefón prijať môžete.
        </T>
      </p>

      <ConfirmDialog
        open={asking}
        onClose={() => setAsking(false)}
        onConfirm={() => void write(true)}
        title={tt('Zablokovať zákazníka?')}
        description={tt('Zablokuje sa jeho e-mail a telefón.')}
        confirmLabel={tt('Zablokovať')}
        cancelLabel={tt('Zrušiť')}
        variant="danger"
        loading={saving}
      >
        <label className="block">
          <span className="block text-xs font-medium mb-1">
            <T>Dôvod (nepovinný)</T>
          </span>
          <input
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            data-customer-block-reason
            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
          />
        </label>
      </ConfirmDialog>
    </div>
  )
}
